import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { folderTypes } from "@redotech/redo-model/views/folders";
import { PurchaseOrderView } from "@redotech/redo-model/views/views";
import {
  ReactNode,
  createContext,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CreatePurchaseOrderViewFolderModal } from "./create-purchase-order-view-folder-modal";
import { EditPurchaseOrderViewFolderModal } from "./edit-purchase-order-view-folder-modal";

export interface PurchaseOrderViewsData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: PurchaseOrderView[],
    updatedFolders: folderTypes["purchase-orders"][],
  ) => void;
  views?: PurchaseOrderView[];
  folders?: folderTypes["purchase-orders"][];
  reloadViews: () => void;
}

export const PurchaseOrderViewsContext = createContext<
  PurchaseOrderViewsData | undefined
>(undefined);

export const PurchaseOrderViewsProvider = memo(
  function PurchaseOrderViewsProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const client = useRequiredContext(RedoInventoryRpcClientContext);

    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showEditFolderModal, setShowEditFolderModal] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<
      folderTypes["purchase-orders"] | null
    >(null);

    const { mutations, ...viewsLoad } = useRpcData(
      client,
      "getPurchaseOrderViewsAndFolders",
      {
        input: null,
        mutations: [
          "createPurchaseOrderViewFolder",
          "updatePurchaseOrderViewFolder",
          "deletePurchaseOrderViewFolder",
          "updatePurchaseOrderViewOrdering",
        ],
      },
    );

    // Store the state separately from the load so we can do optimistic updates
    const [views, setViews] = useState<PurchaseOrderView[]>([]);
    const [folders, setFolders] = useState<folderTypes["purchase-orders"][]>(
      [],
    );
    useEffect(() => {
      if (viewsLoad.value) {
        setViews([...viewsLoad.value.views]);
        setFolders([...viewsLoad.value.folders]);
      }
    }, [viewsLoad.value]);

    const handleCreateFolder = useHandler(async (name: string) => {
      await mutations.createPurchaseOrderViewFolder({ name });
    });

    const handleEditFolder = useHandler(
      async (folderId: string, folderName: string) => {
        await mutations.updatePurchaseOrderViewFolder({
          folderId,
          name: folderName,
        });
      },
    );

    const handleDeleteFolder = useHandler(async (folderId: string) => {
      await mutations.deletePurchaseOrderViewFolder({ folderId });
    });

    const updateOrdering = useHandler(
      async (
        updatedViews: PurchaseOrderView[],
        updatedFolders: folderTypes["purchase-orders"][],
      ) => {
        setViews(updatedViews);
        setFolders(updatedFolders);

        try {
          await mutations.updatePurchaseOrderViewOrdering({
            views: updatedViews,
            folders: updatedFolders,
          });
        } catch (error) {
          console.error("Error saving view and folder ordering", error);
        }
      },
    );

    const openCreateFolderModal = useHandler(() => {
      setShowCreateFolderModal(true);
    });

    const openEditFolderModal = useHandler((folderId: string) => {
      const folder = folders.find((f) => f._id === folderId);
      if (folder) {
        setSelectedFolder(folder);
        setShowEditFolderModal(true);
      }
    });

    const reservedFolderNames = useMemo(() => {
      const names = new Set(folders.map((f) => f.name));
      if (selectedFolder) {
        names.delete(selectedFolder.name);
      }
      return names;
    }, [folders, selectedFolder]);

    const contextValue: PurchaseOrderViewsData = {
      openCreateFolderModal,
      openEditFolderModal,
      handleDeleteFolder,
      updateOrdering,
      views,
      folders,
      reloadViews: () => viewsLoad.refresh(true),
    };

    return (
      <PurchaseOrderViewsContext.Provider value={contextValue}>
        {children}
        <CreatePurchaseOrderViewFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
          reservedFolderNames={reservedFolderNames}
        />
        {selectedFolder && (
          <EditPurchaseOrderViewFolderModal
            deleteFolder={handleDeleteFolder}
            isOpen={showEditFolderModal}
            onClose={() => setShowEditFolderModal(false)}
            onSubmit={handleEditFolder}
            reservedFolderNames={reservedFolderNames}
            selectedFolder={selectedFolder}
          />
        )}
      </PurchaseOrderViewsContext.Provider>
    );
  },
);
